import React, { useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
  Box,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px",
    },
    headerCell: {
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    stickyYearCell: {
      position: "sticky",
      left: 0,
      width: "150px",
      minWidth: "100px",
      backgroundColor: theme.palette.background.default,
      zIndex: 1,
      borderRadius: "10px",
      fontWeight: "bold",
      borderBottom: "none",
    },
    bodyRow: {
      "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.background.paper,
      },
      "&:nth-of-type(even)": {
        backgroundColor: theme.palette.background.entity_background,
      },
    },
    tableWrapper: {
      overflowX: "auto",
    },
  }),
);

interface MonthlySalesTableProps {
  nodes: any;
}

const ChartSalesTable: React.FC<MonthlySalesTableProps> = ({ nodes }) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();

  // gradient color scale (fixed to use min/max instead of index)
  const getColor = (value: number, list: number[]) => {
    if (value === 0) return theme.palette.background.entity_background;
    const output = list.filter((num) => num !== 0);
    const startColor = { r: 222, g: 255, b: 235 }; // light
    const endColor = { r: 2, g: 105, b: 87 }; // dark

    const min = Math.min(...output);
    const max = Math.max(...output);

    const ratio = max === min ? 0 : (value - min) / (max - min);

    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  // check contrast
  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;
    if (color === "#FCFCFC") return false;

    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    }
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128;
  };

  const [data, setData] = React.useState<any>([]);
  const [valueTotal, setValueTotal] = React.useState<number[]>([]);

  const normalizeDate = (dateStr: string) => {
    // remove extra spaces after comma
    return new Date(dateStr.replace(/\s{2,}/g, " "));
  };

  useEffect(() => {
    if (nodes?.nodes) {
      const outputMap: any = {};
      const valueTotalArr: number[] = [];

      nodes.nodes.forEach((n: any) => {
        const date = normalizeDate(n.date);
        const year = date.getFullYear();
        const monthName = date.toLocaleString("en-US", { month: "long" });
        const dayNum = date.getDate();

        if (!outputMap[year]) {
          outputMap[year] = {};
        }
        if (!outputMap[year][monthName]) {
          const daysArray = Array.from({ length: 31 }, (_, i) => ({
            day: i + 1,
            count: 0,
            value: 0,
          }));
          outputMap[year][monthName] = { month: monthName, data: daysArray };
        }

        outputMap[year][monthName].data[dayNum - 1] = {
          day: dayNum,
          count: n.orders,
          value: Number(n.total),
        };

        valueTotalArr.push(Number(n.total));
      });

      const monthOrder = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      // Convert to array of { year, months: [...] }
      const sortedData = Object.keys(outputMap)
        .sort((a, b) => Number(a) - Number(b)) // sort years ascending
        .map((year) => {
          const months = Object.values(outputMap[year]).sort(
            (a: any, b: any) =>
              monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month),
          );
          return { year, months };
        });

      setData(sortedData);
      setValueTotal(valueTotalArr);
    }
  }, [nodes.nodes]);

  const a = [
    {
      average: "0.00",
      card: "0.00",
      cash: "0.00",
      date: "Dec 31,  2024",
      discount: "0.00",
      max: "0.00",
      min: "0.00",
      orders: 0,
      otherPayment: "0.00",
      subTotal: "0.00",
      total: "0.00",
    },
    {
      average: "0.00",
      card: "0.00",
      cash: "0.00",
      date: "Dec 30,  2024",
      discount: "0.00",
      max: "0.00",
      min: "0.00",
      orders: 0,
      otherPayment: "0.00",
      subTotal: "0.00",
      total: "0.00",
    },
    {
      average: "0.00",
      card: "0.00",
      cash: "0.00",
      date: "Dec 29,  2024",
      discount: "0.00",
      max: "0.00",
      min: "0.00",
      orders: 0,
      otherPayment: "0.00",
      subTotal: "0.00",
      total: "0.00",
    },
  ];
  return (
    <Box className={classes.tableWrapper}>
      <Table className={classes.table} size="small" stickyHeader>
        <TableHead>
          {/* Year row */}
          <TableRow>
            <TableCell
              className={classes.stickyYearCell}
              style={{ zIndex: 200 }}
            >
              Day
            </TableCell>
            {data.map((yearData: any, i: number) => (
              <TableCell
                key={i}
                align="center"
                className={classes.headerCell}
                colSpan={yearData.months.length}
              >
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  {yearData.year}
                </Typography>
              </TableCell>
            ))}
          </TableRow>

          {/* Month row */}
          <TableRow>
            <TableCell className={classes.stickyYearCell}></TableCell>
            {data.map((yearData: any, i: number) =>
              yearData.months.map((month: any, j: number) => (
                <TableCell
                  key={`${i}-${j}`}
                  align="center"
                  className={classes.headerCell}
                >
                  {month.month}
                </TableCell>
              )),
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Loop over 31 days */}
          {Array.from({ length: 31 }, (_, dayIndex) => {
            const day = dayIndex + 1;
            return (
              <TableRow key={day} className={classes.bodyRow}>
                <TableCell className={classes.stickyYearCell}>{day}</TableCell>

                {data.map((yearData: any, i: any) =>
                  yearData.months.map((month: any, j: number) => {
                    const cell = month.data[dayIndex];
                    const bgColor = getColor(cell.value, valueTotal);

                    return (
                      <TableCell
                        key={`${i}-${j}`}
                        align="center"
                        style={{
                          borderBottom: "none",
                          padding: "0px",
                          borderRadius: "10px",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: bgColor,
                            borderRadius: "10px",
                            height: "42px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            style={{
                              color: isDarkColor(bgColor) ? "white" : "black",
                              fontWeight: "bold",
                            }}
                          >
                            {cell.value === 0 ? "-" : cell.value.toFixed(2)}
                          </Typography>
                          <Typography
                            style={{
                              color: isDarkColor(bgColor) ? "white" : "black",
                            }}
                            variant="caption"
                          >
                            {cell.count}
                          </Typography>
                        </div>
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ChartSalesTable;
